import { useEffect, useRef } from "react";

/* Lo "stormo": sciame di particelle che insegue un attrattore mobile con onde
   di colore (estratto dalla NebulaHero). Si pausa fuori viewport. */
export const FlockField = ({ className }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: false });
    let raf = null, running = false, W = 0, H = 0, t = 0, frame = 0;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const P = [];
    const BG = "#09090B";

    const resize = () => {
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, W, H);
    };
    resize();
    window.addEventListener("resize", resize);

    const N = Math.max(900, Math.min(3200, Math.round((W * H) / 520)));
    const spread = Math.max(300, W / 4);
    for (let i = 0; i < N; i++) {
      P.push({
        x: W / 2 + (Math.random() - 0.5) * spread, y: H / 2 + (Math.random() - 0.5) * spread,
        vx: (Math.random() - 0.5) * 2, vy: (Math.random() - 0.5) * 2,
        s: 2 + Math.random() * 2, o: Math.random() * 6.28,
      });
    }

    let emaFrame = 16.7;
    let last = performance.now();
    const styleCache = new Array(32 * 8);

    const draw = () => {
      if (!running) return;
      const now = performance.now();
      const dt = Math.min(50, now - last);
      last = now;
      emaFrame = emaFrame * 0.9 + dt * 0.1;
      frame += 1;
      if (frame % 120 === 60 && emaFrame > 21 && P.length > 700) {
        P.length = Math.floor(P.length * 0.8);
      }
      t += 0.014 * Math.min(2, Math.max(0.6, dt / 16.7));
      const wide = W / H > 1.9;
      const ampX = wide ? 0.3 : 0.2;
      const A = {
        x: W * (0.5 + ampX * Math.sin(t * 0.33) + 0.06 * Math.sin(t * 0.91 + 2)),
        y: H * (0.5 + 0.16 * Math.cos(t * 0.27 + 1) + 0.05 * Math.sin(t * 0.71)),
      };
      const S = Math.sqrt(W * H);
      const R = S * (0.15 + 0.06 * Math.sin(t * 0.47));
      const ex = 1 + 0.55 * Math.sin(t * 0.19), ey = 1 / (1 + 0.35 * Math.sin(t * 0.19));
      const hue = (225 + 75 * Math.sin(t * 0.07) + 30 * Math.sin(t * 0.023 + 2) + 360) % 360;
      ctx.globalCompositeOperation = "source-over";
      if (frame % 420 === 0) {
        ctx.fillStyle = BG;
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.fillStyle = "rgba(9,9,11,0.42)";
        ctx.fillRect(0, 0, W, H);
      }
      ctx.globalCompositeOperation = "lighter";
      styleCache.fill(undefined);
      const tw = t * 1.6, ph1 = t * 0.23;
      for (const p of P) {
        const dx = (A.x - p.x) * ex, dy = (A.y - p.y) * ey;
        const d = Math.sqrt(dx * dx + dy * dy) + 1;
        const spring = (d - R) * 0.0012;
        const sw = 0.9 * Math.sin(ph1 + p.o);
        const nx = Math.sin(p.y * 0.011 + t * 1.7 + p.o) + Math.cos(p.x * 0.009 - t * 1.2);
        const ny = Math.cos(p.x * 0.010 + t * 1.5) - Math.sin(p.y * 0.012 + t * 0.8 + p.o);
        p.vx += dx / d * spring * 3 + (-dy / d) * sw * 0.09 + nx * 0.11;
        p.vy += dy / d * spring * 3 + (dx / d) * sw * 0.09 + ny * 0.11;
        const v = Math.sqrt(p.vx * p.vx + p.vy * p.vy) || 1;
        p.vx = p.vx / v * (v * 0.85 + p.s * 0.15);
        p.vy = p.vy / v * (v * 0.85 + p.s * 0.15);
        p.x += p.vx; p.y += p.vy;
        const wave = 35 * Math.sin(tw - d * 0.015);
        const sp = Math.min(1, v * 0.22);
        const hi = Math.round((((hue + wave + sp * 30) % 360) + 360) % 360 / 11.25) % 32;
        const si = Math.min(7, (sp * 8) | 0);
        const key = hi * 8 + si;
        let style = styleCache[key];
        if (style === undefined) {
          style = `hsla(${hi * 11.25},85%,${62 + si * 2.6}%,${0.45 + si * 0.052})`;
          styleCache[key] = style;
        }
        ctx.fillStyle = style;
        ctx.fillRect(p.x, p.y, 2, 2);
      }
      raf = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running) return;
      running = true; last = performance.now();
      raf = requestAnimationFrame(draw);
    };
    const stop = () => { running = false; if (raf) cancelAnimationFrame(raf); };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const io = new IntersectionObserver(([e]) => (e.isIntersecting && !reduced ? start() : stop()), { threshold: 0.05 });
    io.observe(canvas);
    return () => { stop(); io.disconnect(); window.removeEventListener("resize", resize); };
  }, []);

  return <canvas ref={canvasRef} data-testid="flock-canvas" className={className} />;
};

export default FlockField;
